import { useMemo, useState } from 'react'
import type { JSX } from 'react'

import { Text } from '@buffer-mono/popcorn'

import type { Service } from '../../graphqlTypes'

import { PostItemsFooter } from './PostItemsFooter'
import { PostItemsTable } from './PostItemsTable'
import { BulkPostDialogBody } from '../BulkPostDialog'
import {
  createPost,
  isError,
  isSuccess,
  isValid,
  parsePostingTime,
  updateItemStatus,
} from './helpers'
import type { ExtraColumn, ListItem, Post } from './helpers'
import { StatusCounters } from './StatusCounters'

export const PostItems = ({
  items,
  setItems,
  channelId,
  channelName,
  twentyFourHourFormat,
  channelTimezone,
  channelService,
  onDialogClose,
}: {
  items: ListItem[]
  setItems: React.Dispatch<React.SetStateAction<ListItem[]>>
  channelId: string
  channelName: string
  twentyFourHourFormat: boolean
  channelTimezone: string
  channelService: Service
  onDialogClose: (closeAction: 'success' | 'cancel') => void
}): JSX.Element => {
  const [creationAttempted, setCreationAttempted] = useState(false)
  const [isCreatingPosts, setIsCreatingPosts] = useState(false)

  const [selectedSaveAction, setSelectedSaveAction] = useState<
    'post' | 'draft'
  >('post')

  const validItems = useMemo(() => {
    return items.filter((item) => isValid(item))
  }, [items])
  const validItemCount = validItems.length

  const successItemCount = useMemo(() => {
    return items.filter((item) => isSuccess(item)).length
  }, [items])

  const errorItems = useMemo(
    () => items.filter((item) => isError(item)),
    [items],
  )

  const extraColumns = useMemo((): ExtraColumn[] => {
    if (channelService === 'pinterest') {
      return [
        {
          key: 'board',
          header: 'Board',
          width: '15%',
          renderCell: (data: Post) =>
            data.board?.name ? <Text>{data.board.name}</Text> : null,
        },
        {
          key: 'title',
          header: 'Title',
          width: '5%',
          renderCell: (data: Post) =>
            data.title ? <Text>{data.title}</Text> : null,
        },
        {
          key: 'alt_text',
          header: 'Alt Text',
          width: '5%',
          renderCell: (data: Post) =>
            data.alt_text ? <Text>{data.alt_text}</Text> : null,
        },
        {
          key: 'source_url',
          header: 'Link',
          width: '5%',
          renderCell: (data: Post) =>
            data.source_url ? <Text>{data.source_url}</Text> : null,
        },
      ]
    }
    return []
  }, [channelService])

  const canStartPostCreation = useMemo(
    () => !creationAttempted && validItemCount > 0,
    [creationAttempted, validItemCount],
  )
  const isPostCreationComplete = useMemo(
    () =>
      creationAttempted &&
      items.every(
        (item) =>
          item.status !== 'loading' &&
          item.status !== 'valid' &&
          (item.status === 'error' || item.status === 'success'),
      ),
    [creationAttempted, items],
  )

  const onCreatePosts = async (type: 'post' | 'draft'): Promise<void> => {
    // Set whether items are being saved as drafts or posts
    setSelectedSaveAction(type)
    setIsCreatingPosts(true)

    // Set all invalid items to error status
    errorItems.forEach((item) => {
      setItems((currentItems) =>
        updateItemStatus(currentItems, item.id, 'error'),
      )
    })

    // Set all valid items to loading status
    validItems.forEach((item) => {
      setItems((currentItems) =>
        updateItemStatus(currentItems, item.id, 'loading'),
      )
    })

    const createValidItem = async (item: ListItem): Promise<void> => {
      try {
        // Parse posting time to scheduling data
        const schedulingData = item.data.postingTime
          ? parsePostingTime(item.data.postingTime, channelTimezone)
          : {}
        // Remove postingTime and altText from the API payload
        // altText needs special handling — it goes inside post.media, not top-level
        const {
          postingTime: _postingTime,
          alt_text: altText,
          ...restPostData
        } = item.data

        const post: Parameters<typeof createPost>[0] = {
          ...restPostData,
          is_draft: type === 'draft',
          ...schedulingData,
        }

        if (channelService === 'pinterest' && restPostData.board) {
          post.subprofile_ids = [restPostData.board.id]
        }

        if (channelService === 'pinterest' && altText && post.media) {
          post.media = { ...post.media, alt_text: altText }
        }

        const response = await createPost(post)
        if (!response.success) {
          throw new Error(response.message)
        }
        setItems((currentItems) =>
          updateItemStatus(currentItems, item.id, 'success'),
        )
      } catch (error) {
        setItems((currentItems) =>
          updateItemStatus(currentItems, item.id, 'error', [
            error instanceof Error ? error.message : 'Unknown error',
          ]),
        )
      }
    }

    const createValidItems = async (index: number): Promise<void> => {
      const item = validItems[index]
      if (!item) return

      await createValidItem(item)
      await createValidItems(index + 1)
    }

    await createValidItems(0)

    setIsCreatingPosts(false)

    // Set creation attempted to true
    setCreationAttempted(true)
  }

  const title = useMemo(() => {
    if (isPostCreationComplete) {
      if (successItemCount === 0) {
        return `Failed to create ${
          selectedSaveAction === 'draft' ? 'drafts' : 'posts'
        }`
      }
      return selectedSaveAction === 'draft'
        ? 'Saving Complete!'
        : 'Scheduling Complete!'
    }
    if (isCreatingPosts) {
      return selectedSaveAction === 'draft'
        ? 'Saving content...'
        : 'Scheduling content...'
    }
    return 'Review Content'
  }, [
    isPostCreationComplete,
    isCreatingPosts,
    successItemCount,
    selectedSaveAction,
  ])

  return (
    <>
      <BulkPostDialogBody title={title}>
        <StatusCounters
          items={items}
          channelService={channelService}
          isCreatingPosts={isCreatingPosts}
          isPostCreationComplete={isPostCreationComplete}
          selectedSaveAction={selectedSaveAction}
        />
        <PostItemsTable
          items={items}
          extraColumns={extraColumns}
          twentyFourHourFormat={twentyFourHourFormat}
          channelTimezone={channelTimezone}
          selectedSaveAction={selectedSaveAction}
        />
      </BulkPostDialogBody>
      <PostItemsFooter
        channelId={channelId}
        channelName={channelName}
        successItemCount={successItemCount}
        validItemCount={validItemCount}
        selectedSaveAction={selectedSaveAction}
        canStartPostCreation={canStartPostCreation}
        isCreatingPosts={isCreatingPosts}
        isPostCreationComplete={isPostCreationComplete}
        onCreatePosts={(postType) => {
          onCreatePosts(postType).catch(() => null)
        }}
        onDialogClose={onDialogClose}
      />
    </>
  )
}
